<template>
  <div>
    <load-profile />
    <n-search-container :fields="searchFields" @search="search" @reset="reset">
      <n-search-input ref="search" v-model="params" :fields="searchFields" />
    </n-search-container>

    <b-card no-body class="mb-0">
      <div class="p-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col v-if="$can('create', permissionType)" class="text-right">
            <b-button v-if="!$can('manage', manageRemotePermission)" variant="primary" :to="{ name: `create-${route}` }">
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="false"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
        >
          <template #cell(name)="data">
              {{ getTranslatedName(data.item) }}
          </template>
          <template #cell(isActive)="data">
            <b-badge
              v-if="data.item.isActive"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge v-else pill variant="danger" badge-glow>
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <template v-if="!$can('manage', manageRemotePermission)">
                <b-button
                  v-if="$can('update', permissionType)"
                  variant="primary"
                  pill
                  size="sm"
                  @click="goToDetail(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  v-if="$can('delete', permissionType)"
                  v-b-tooltip.hover
                  :title="$t('general.delete')"
                  variant="danger"
                  pill
                  size="sm"
                  @click="confirmDestroy(data.item.id, data.index, data.item)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </template>
              <b-button
                v-else
                v-b-tooltip.hover
                :title="$t('general.not_allow_to_modify')"
                variant="secondary"
                pill
                size="sm"
              >
                <feather-icon icon="LockIcon" />
              </b-button>
            </div>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
  import {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormGroup,
    BInputGroup,
    BBadge,
    BLink,
    BSpinner
  } from 'bootstrap-vue';
  import vSelect from 'vue-select';
  import Repository from '@/repositories/RepositoryFactory';
  import NPagination from '@/components/NPagination';
  import NSearchContainer from '@/components/NSearchContainer';
  import NSearchInput from '@/components/NSearchInput';
  import NTable from '@/components/NTable';
  import useJwt from '@/auth/jwt/useJwt';
  import SearchInputs from './searchInput';
  import tableFields from './tableField';

  const ProjectRepository = Repository.get('project');

  export default {
    components: {
      BCard,
      BRow,
      BCol,
      BFormInput,
      BButton,
      BLink,
      BFormGroup,
      BInputGroup,
      BBadge,
      BSpinner,
      NPagination,
      NSearchInput,
      NSearchContainer,
      NTable,
      vSelect
    },

    data() {
      return {
        items: [],
        loading: false,
        total: 0,
        perPageOptions: this.$store.state.pagination.perPageOptions,
        params: {
          name: this.$route.query.name || null,
          isActive: this.$route.query.isActive || null
        },
        query: {
          page: Number(this.$route.query.page) || 1,
          order: this.$route.query.order || null,
          sort: this.$route.query.sort || null,
          name: this.$route.query.name || null,
          isActive: this.$route.query.isActive || null
        }
      };
    },

    computed: {
      userData() {
        return this.$store.state.profile.email || this.$store.state.profile.phone  
          ? this.$store.state.profile
          : JSON.parse(
              localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
            );
      },
      perPage: {
        get() {
          return this.$store.state.pagination.perPage;
        },
        set(value) {
          this.$store.commit('pagination/SET_PER_PAGE', value);
        }
      },
      activeSearch() {
        return !!this.$route.query.name || !!this.$route.query.isActive;
      },
      locale() {
        return this.$i18n.locale.split('-')[0];
      }
    },

    watch: {
      perPage(value) {
        this.list(1);
      }
    },

    mounted() {
      this.getData(this.query.page);
    },

    methods: {
      getTranslatedName(data) {
        let name = data?.translation?.name
        if (!name) name = data?.name
        if (typeof name === 'object') {
          name = name[this.locale] || name.en || name.zh || name.km || name.vi || '-';
        }
        return name || '-';
      },
      updateQuerySting() {
        this.$router.replace({ query: this.query });
      },
      list(page = 1) {
        this.query.page = page;
        this.updateQuerySting();
        this.getData();
      },
      sortChanged(sortBy, sortDirection) {
        this.query.order = sortBy;
        this.query.sort = sortDirection;
        this.updateQuerySting();
        this.getData();
      },
      search() {
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQuerySting();
        this.getData();
      },
      reset() {
        this.$refs.search.reset();
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQuerySting();
        this.getData();
      },
      getData() {
        this.loading = true;
        ProjectRepository.list({
          ...this.query,
          name: this.query.name?.trim() || null
        })
          .then(response => {
            const data = response.data.data.list;
            this.items = [...data];
            this.total = response.data.data.total;
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
          });
      },
      destroy(id) {
        ProjectRepository.destroy(id)
          .then(response => {
            this.getData();
          })
          .catch(error => {})
          .then(() => {
            this.loading = false;
          });
      },
      goToDetail(data) {
        this.$router.push({
          name: 'update-project',
          params: {
            id: data.id
          }
        });
      },
      confirmDestroy(id, index, data) {
        index = this.$refs.table.calculateOrderNumber(index);

        this.$bvModal
          .msgBoxConfirm(
            this.$t('alert.unableToRevertAfterDelete', {
              item: `#${index} ${this.getTranslatedName(data)}`
            }),
            {
              title: this.$t('alert.confirmContinueOperation'),
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: this.$t('button.yesDelete'),
              cancelTitle: this.$t('button.cancel'),
              cancelVariant: 'outline-secondary',
              hideHeaderClose: false,
              centered: true
            }
          )
          .then(value => {
            if (value) {
              this.destroy(id);
            }
          });
      }
    },

    setup() {
      const fields = tableFields;
      const searchFields = SearchInputs;
      const permissionType = 'manage-project';
      const route = 'project';
      const manageRemotePermission = 'manage-remote-project';

      return {
        fields,
        searchFields,
        permissionType,
        route,
        manageRemotePermission,
      };
    }
  };
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';

  .link-hover:hover {
    text-decoration: underline;
  }
</style>
